// The spend datapoints overlaid on a saturation curve: one per model period (a week or a
// month, per reference_model.config.saturation_granularity), either the model's historical
// spend (Model Report) or a plan's spend (plan details, comparator, What-If).
//
// A weekly model with a few years of history draws hundreds of them, so the section offers a
// period collapser. Collapsing AVERAGES the spends of each bucket rather than summing them:
// the curve's x axis is spend PER MODEL PERIOD, so a monthly total of a weekly model would sit
// far outside the curve's x range and every point would pile up against its right edge with a
// clamped y. The average keeps x on the curve's own scale, so the collapsed point reads
// exactly like the individual ones, only less noisy.
import moment from 'moment'

// Canonical vocabulary, same as ModelReportComponent.normalizeByFreq uses for the report's
// by-frequency payloads.
export const PERIOD_COLLAPSE_ORDER = ['weekly', 'monthly', 'quarterly', 'yearly']

// The selector's own first option: draw no datapoints at all. It is where the section starts,
// and it is what the "show datapoints" switch used to be when off.
export const NONE_COLLAPSE = 'none'

// Linear interpolation of the saturation curve (xs, ys) at spend x, clamped to the curve's
// range so a spend value always lands on the drawn curve.
export function interpolateCurveValue (xs, ys, x) {
  if (!Array.isArray(xs) || !Array.isArray(ys) || xs.length === 0) {
    return 0
  }
  if (x <= xs[0]) {
    return ys[0]
  }
  if (x >= xs[xs.length - 1]) {
    return ys[ys.length - 1]
  }
  for (let i = 0; i < xs.length - 1; i++) {
    if (x >= xs[i] && x <= xs[i + 1]) {
      const dx = xs[i + 1] - xs[i]
      if (dx === 0) {
        return ys[i]
      }
      const t = (x - xs[i]) / dx
      return ys[i] + t * (ys[i + 1] - ys[i])
    }
  }
  return ys[ys.length - 1]
}

// The model's own granularity: the finest collapse it can offer, and the identity one — the
// datapoints already come one per model period.
export function defaultCollapseOption (isWeeklyModel) {
  return isWeeklyModel ? 'weekly' : 'monthly'
}

// The selector's options, in order. Weekly is offered only for weekly models; a monthly model
// cannot be split back into weeks.
export function collapseOptionsFor (isWeeklyModel) {
  const periods = isWeeklyModel
    ? PERIOD_COLLAPSE_ORDER.slice()
    : PERIOD_COLLAPSE_ORDER.slice(1)
  return [NONE_COLLAPSE].concat(periods)
}

// Bucket a YYYY-MM-DD datapoint date. Weekly keeps the date itself (one bucket per point).
export function bucketKeyFor (dateString, option) {
  if (option === 'weekly') {
    return String(dateString)
  }
  const date = moment(dateString)
  if (!date.isValid()) {
    return String(dateString)
  }
  if (option === 'yearly') {
    return date.format('YYYY')
  }
  if (option === 'quarterly') {
    return date.format('YYYY') + '-Q' + date.quarter()
  }
  return date.format('YYYY-MM')
}

// Collapse one channel's datapoints. The channel is returned untouched when it carries no
// datapoints (the curves still draw, just without the overlay) or no dates to bucket them by.
export function collapseChannelDatapoints (channel, option) {
  if (!channel || !Array.isArray(channel.datapoints) || !Array.isArray(channel.datapoints_dates)) {
    return channel
  }
  const buckets = []
  const byKey = {}
  channel.datapoints.forEach((point, index) => {
    const date = channel.datapoints_dates[index]
    if (!point || date === undefined) return
    const key = bucketKeyFor(date, option)
    if (!byKey[key]) {
      byKey[key] = { xs: [], date: date }
      buckets.push(byKey[key])
    }
    byKey[key].xs.push(typeof point.x === 'number' ? point.x : 0)
    // The bucket is labelled by its last date, following the period-end convention the model's
    // own axis uses (month ends in dates.all, the closing Sunday of each week).
    if (String(date) > String(byKey[key].date)) {
      byKey[key].date = date
    }
  })
  const datapoints = []
  const datapointsDates = []
  buckets.forEach(bucket => {
    const sum = bucket.xs.reduce((a, b) => a + b, 0)
    const x = bucket.xs.length > 0 ? sum / bucket.xs.length : 0
    datapoints.push({
      x: x,
      // Recomputed rather than averaged, so the collapsed point sits exactly on the drawn
      // curve — the same thing the plan overlays do with their own spend.
      y: interpolateCurveValue(channel.x, channel.y, x)
    })
    datapointsDates.push(bucket.date)
  })
  const collapsed = {}
  Object.keys(channel).forEach(key => { collapsed[key] = channel[key] })
  collapsed.datapoints = datapoints
  collapsed.datapoints_dates = datapointsDates
  return collapsed
}

// Collapse every channel of a (flat, already group-resolved) saturation object. Returns the
// SAME object for the two identity cases — no collapse selected, or the model's own
// granularity — so they cost nothing and change no reference downstream.
export function collapseSaturationDatapoints (saturationData, option, nativeOption) {
  if (!saturationData || !option || option === NONE_COLLAPSE || option === nativeOption) {
    return saturationData
  }
  const collapsed = {}
  Object.keys(saturationData).forEach(channel => {
    collapsed[channel] = collapseChannelDatapoints(saturationData[channel], option)
  })
  return collapsed
}

// True when at least one channel carries datapoints that can actually be collapsed — points
// AND the dates to bucket them by. The period selector is shown on this, and not on the
// datapoints switch's own condition (regions), which is unrelated.
export function hasSaturationDatapoints (effectiveData) {
  if (!effectiveData) return false
  return Object.keys(effectiveData).some(channel => {
    const c = effectiveData[channel]
    return !!(c && Array.isArray(c.datapoints) && c.datapoints.length > 0 &&
      Array.isArray(c.datapoints_dates) && c.datapoints_dates.length > 0)
  })
}
